import getConfig from 'next/config'

import type { Context as CartContext } from '@grandvisionhq/carts-v2'
import type {
  AddressLookupOptions,
  CheckoutSettings,
  Consent,
  Routes,
} from '@grandvisionhq/checkout-v2'
import { AddressLineFormat } from '@grandvisionhq/checkout-v2'
import type { IntlContextModel } from '@grandvisionhq/state'
import { Util as StoreUtils } from '@grandvisionhq/stores-v2'

interface SettingsInput {
  cart: CartContext.CartContextModel['cart']
  intl: IntlContextModel
}

const { publicRuntimeConfig } = getConfig()
const { orderTimeOut } = publicRuntimeConfig.checkout

export const basePath = '/checkout'
export const routes: Routes = {
  shipping: {
    label: 'Versand',
    path: `${basePath}/versand`,
  },
  overview: {
    label: 'Zusammenfassung',
    path: `${basePath}/zusammenfassung`,
  },
  payment: {
    label: 'Zahlung',
    path: `${basePath}/zahlung`,
  },
}

const getCheckoutConsents = ({
  cart,
  intl,
}: {
  cart: CartContext.CartContextModel['cart']
  intl: IntlContextModel
}): Consent[] => {
  const { getLabel, getFormattedLabel } = intl
  const hasContactLenses = cart?.contains.contactLenses
  const hasPrescriptionGlasses = cart?.contains.prescriptionGlasses

  const allConsents: Consent[] = [
    {
      title:
        getFormattedLabel('checkout.consent.conditions', {
          parseLink: true,
          openLinksInNewTab: true,
          parseHTML: false,
        }) + '&nbsp;*',
      value: 'terms-conditions',
      required: true,
      errorMessage: getLabel('checkout.consent.conditions.error'),
    },
  ]

  if (hasContactLenses || hasPrescriptionGlasses) {
    allConsents.push({
      title: getLabel('checkout.consent.prescription') + '&nbsp;*',
      value: 'prescription-consent',
      required: true,
      errorMessage: getLabel('checkout.consent.prescription.error'),
    })
  }

  return allConsents
}

export const getCheckoutConfig = ({ cart, intl }: SettingsInput): CheckoutSettings => {
  const accountSettings = {
    createAccountRequired: (!!cart && cart.isAccountRequired) || cart?.contains.subscription,
    implicitAccountCreation: false,
  }

  const addressLookup: AddressLookupOptions = {
    addressLineFormat: AddressLineFormat.streetName_streetNumber,
    shouldCombineAddress: true,
  }

  const formFieldsMap = {
    billingAddress: [
      { field: 'firstName', required: true },
      { field: 'lastName', required: true },
      { field: 'streetName', required: true },
      { field: 'additionalAddressInfo', required: false },
      { field: 'additionalStreetInfo', required: false },
      { field: 'city', required: true },
      { field: 'postalCode', required: true },
      { field: 'salutation', required: true },
    ],
    deliveryAddress: [
      { field: 'firstName', required: true },
      { field: 'lastName', required: true },
      { field: 'streetName', required: true },
      { field: 'additionalAddressInfo', required: false },
      { field: 'additionalStreetInfo', required: false },
      { field: 'city', required: true },
      { field: 'postalCode', required: true },
      { field: 'salutation', required: true },
    ],
    contactDetails: [
      { field: 'email', required: true },
      { field: 'phone', required: false },
      {
        field: 'dateOfBirth',
        required: true,
      },
    ],
  }
  return {
    formFieldsMap,
    account: accountSettings,
    consent: {
      implicitTermsAndConditions: false,
      items: getCheckoutConsents({ intl, cart }),
    },
    addressLookup,
    shippingDetails: {
      loadingFinished: Boolean(cart) || cart === null,
      ...(cart?.billingAddress ? { billingAddress: cart.billingAddress } : {}),
      ...(cart?.shippingAddress ? { deliveryAddress: cart.shippingAddress } : {}),
      ...(cart?.customerDetails ? { customer: cart.customerDetails } : {}),
    },
    shippingFormFields: {
      billingAddress: [
        'additionalAddressInfo',
        'additionalStreetInfo',
        'city',
        'country',
        'firstName',
        'lastName',
        'postalCode',
        'salutation',
        'streetName',
      ],
      contactDetails: ['email', 'phone', 'dateOfBirth'],
    },
    shipping: {
      requiredShippingCountry: 'AT',
    },
    serviceStore: {
      isOptional: true,
    },
    storeFinder: {
      showUseMyLocation: true,
      searchWhitelist: ['storeName', 'postalCode', 'streetName', 'town'] as StoreUtils.SearchKey[],
    },
    ...(orderTimeOut ? { orderCreationTimeOutLimit: orderTimeOut } : {}),
    features: {
      isCompanyEnabled: false,
      isServiceStoreEnabled: true,
    },
    login: {
      disableSignup: true,
      returnPath: cart ? `${routes.shipping?.path}?cartId=${cart.id}` : routes.shipping?.path,
    },
    routes,
    payments: {
      applePay: {
        merchantName: 'Pearle',
      },
    },
    logoVariant: 'main',
  }
}
